/**
 * NoiseSuppressionEffect: RNNoise 기반 오디오 노이즈 제거 효과 (설정 가능)
 *
 * lib-jitsi-meet의 JitsiLocalTrack.setEffect() 인터페이스 구현.
 *
 * 오디오 처리 그래프 (mixRatio < 1.0 — wet/dry 블렌딩 모드):
 *
 *                    ┌── wetGain ── NoiseSuppressorWorklet ──┐
 *   Source ──────────┤                                       ├── merger ── [HighPass] ── [LowPass] ── [Compressor] ── outputGain ── Destination
 *                    └── dryGain ───────────────────────────┘
 *
 * mixRatio === 1.0 (기본값, RNNoise만 — 단순 체인):
 *   Source ── NoiseSuppressorWorklet ── [HighPass] ── [LowPass] ── [Compressor] ── outputGain ── Destination
 */

import {
  type NoiseSuppressionConfig,
  DEFAULT_NOISE_SUPPRESSION_CONFIG,
} from '@/conference/config/noiseSuppression';

const LOG = '[RNNoise]';

let audioContext: AudioContext | null = null;

export class NoiseSuppressionEffect {
  private _audioSource: MediaStreamAudioSourceNode | null = null;
  private _audioDestination: MediaStreamAudioDestinationNode | null = null;
  private _noiseSuppressorNode: AudioWorkletNode | null = null;
  private _originalMediaTrack: MediaStreamTrack | null = null;
  private _outputMediaTrack: MediaStreamTrack | null = null;

  // 설정 가능 노드 (런타임 조절용)
  private _wetGain: GainNode | null = null;
  private _dryGain: GainNode | null = null;
  private _outputGainNode: GainNode | null = null;
  private _highPassNode: BiquadFilterNode | null = null;
  private _lowPassNode: BiquadFilterNode | null = null;
  private _compressorNode: DynamicsCompressorNode | null = null;

  private _config: NoiseSuppressionConfig;

  constructor(config?: Partial<NoiseSuppressionConfig>) {
    this._config = { ...DEFAULT_NOISE_SUPPRESSION_CONFIG, ...config };
    console.log(`${LOG} Effect 생성 — config:`, JSON.stringify(this._config));
  }

  // ─── setEffect 인터페이스 ─────────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isEnabled(sourceLocalTrack: any): boolean {
    const result = sourceLocalTrack.isAudioTrack();
    console.log(`${LOG} isEnabled() → ${result}`);
    return result;
  }

  startEffect(audioStream: MediaStream): MediaStream {
    console.log(`${LOG} startEffect() — stream active: ${audioStream.active}`);

    try {
      this._originalMediaTrack = audioStream.getAudioTracks()[0];
    } catch (e) {
      console.error(`${LOG} 원본 트랙 접근 실패:`, e);
      return audioStream;
    }
    if (!this._originalMediaTrack) return audioStream;

    try {
      console.log(`${LOG} input settings:`, JSON.stringify(this._originalMediaTrack.getSettings?.() ?? {}));
    } catch { /* ignore */ }

    // AudioContext
    try {
      if (!audioContext || audioContext.state === 'closed') {
        try {
          audioContext = new AudioContext({ sampleRate: 48000 });
        } catch {
          audioContext = new AudioContext();
        }
        console.log(`${LOG} AudioContext 생성 — sampleRate: ${audioContext.sampleRate}`);
      }
    } catch (e) {
      console.error(`${LOG} AudioContext 생성 실패:`, e);
      return audioStream;
    }

    if (!audioStream.active) return audioStream;

    try {
      this._audioSource = audioContext.createMediaStreamSource(audioStream);
    } catch (e) {
      console.error(`${LOG} MediaStreamSource 실패:`, e);
      return audioStream;
    }

    this._audioDestination = audioContext.createMediaStreamDestination();
    try {
      this._outputMediaTrack = this._audioDestination.stream.getAudioTracks()[0];
    } catch {
      return audioStream;
    }

    // 오디오 그래프 구축 (비동기 — Worklet 로드)
    this._buildAudioGraph()
      .then(() => {
        console.log(`${LOG} ✅ 오디오 그래프 구축 완료`);
      })
      .catch((e) => {
        console.error(`${LOG} ❌ 오디오 그래프 구축 실패:`, e);
      });

    // 뮤트 상태 동기화
    try {
      this._outputMediaTrack.enabled = this._originalMediaTrack.enabled;
      this._originalMediaTrack.enabled = true;
    } catch { /* ignore */ }

    return this._audioDestination.stream;
  }

  stopEffect(): void {
    console.log(`${LOG} stopEffect()`);

    try {
      if (this._originalMediaTrack && this._outputMediaTrack) {
        this._originalMediaTrack.enabled = this._outputMediaTrack.enabled;
      }
    } catch { /* ignore */ }

    try { this._noiseSuppressorNode?.port?.close(); } catch { /* */ }

    // 모든 노드 disconnect
    const nodes = [
      this._audioDestination,
      this._noiseSuppressorNode,
      this._audioSource,
      this._wetGain,
      this._dryGain,
      this._outputGainNode,
      this._highPassNode,
      this._lowPassNode,
      this._compressorNode,
    ];
    for (const node of nodes) {
      try { node?.disconnect(); } catch { /* */ }
    }

    if (audioContext) {
      audioContext.close().catch(() => {});
      audioContext = null;
    }

    this._audioSource = null;
    this._audioDestination = null;
    this._noiseSuppressorNode = null;
    this._originalMediaTrack = null;
    this._outputMediaTrack = null;
    this._wetGain = null;
    this._dryGain = null;
    this._outputGainNode = null;
    this._highPassNode = null;
    this._lowPassNode = null;
    this._compressorNode = null;
  }

  // ─── 런타임 설정 변경 ──────────────────────────────────────────────────

  /**
   * 런타임에 설정을 변경한다.
   * 오디오 그래프를 재구축하지 않고 기존 노드의 파라미터만 업데이트.
   */
  updateConfig(partial: Partial<NoiseSuppressionConfig>): void {
    Object.assign(this._config, partial);
    console.log(`${LOG} updateConfig:`, JSON.stringify(this._config));

    if (!audioContext) return;
    const t = audioContext.currentTime;

    // mix ratio
    if (this._wetGain && this._dryGain) {
      this._wetGain.gain.setValueAtTime(this._config.mixRatio, t);
      this._dryGain.gain.setValueAtTime(1 - this._config.mixRatio, t);
      console.log(`${LOG} mixRatio → ${this._config.mixRatio}`);
    }

    // output gain
    if (this._outputGainNode) {
      this._outputGainNode.gain.setValueAtTime(this._config.outputGain, t);
      console.log(`${LOG} outputGain → ${this._config.outputGain}`);
    }

    // high pass filter
    if (this._highPassNode) {
      if (this._config.highPassFilter) {
        this._highPassNode.frequency.setValueAtTime(this._config.highPassFrequency, t);
        // Q 값이 극단적으로 높으면 공진 발생 — 0.707이 Butterworth 기본값
        this._highPassNode.Q.setValueAtTime(0.707, t);
      } else {
        // 비활성화: 주파수를 0으로 내려 모든 주파수 통과
        this._highPassNode.frequency.setValueAtTime(0, t);
      }
      console.log(`${LOG} highPass → ${this._config.highPassFilter ? this._config.highPassFrequency + 'Hz' : 'OFF'}`);
    }

    // low pass filter
    if (this._lowPassNode) {
      if (this._config.lowPassFilter) {
        this._lowPassNode.frequency.setValueAtTime(this._config.lowPassFrequency, t);
        this._lowPassNode.Q.setValueAtTime(0.707, t);
      } else {
        this._lowPassNode.frequency.setValueAtTime(this._getLowPassBypassFrequency(), t);
      }
      console.log(`${LOG} lowPass → ${this._config.lowPassFilter ? this._config.lowPassFrequency + 'Hz' : 'OFF'}`);
    }

    this._noiseSuppressorNode?.port?.postMessage({
      type: 'config',
      voiceGate: this._createVoiceGateConfig(),
    });

    // compressor
    if (this._compressorNode) {
      if (this._config.compressor) {
        this._compressorNode.threshold.setValueAtTime(this._config.compressorThreshold, t);
      } else {
        // 비활성화: threshold를 0dB로 올려 압축 없음
        this._compressorNode.threshold.setValueAtTime(0, t);
      }
      console.log(`${LOG} compressor → ${this._config.compressor ? this._config.compressorThreshold + 'dB' : 'OFF'}`);
    }
  }

  getConfig(): NoiseSuppressionConfig {
    return { ...this._config };
  }

  // ─── 내부: 오디오 그래프 ────────────────────────────────────────────────

  private async _buildAudioGraph(): Promise<void> {
    if (!audioContext || !this._audioSource || !this._audioDestination) return;

    const ctx = audioContext;
    const workletNode = await this._initializeWorklet();
    this._noiseSuppressorNode = workletNode ?? null;

    // 후처리 체인 노드 생성
    const postChainHead = this._createPostProcessingChain(ctx);

    // 출력 게인
    this._outputGainNode = ctx.createGain();
    this._outputGainNode.gain.value = this._config.outputGain;

    const useMix = this._config.mixRatio < 1.0;

    if (this._noiseSuppressorNode) {
      if (useMix) {
        // ── wet/dry 블렌딩 모드 ──
        this._wetGain = ctx.createGain();
        this._wetGain.gain.value = this._config.mixRatio;

        this._dryGain = ctx.createGain();
        this._dryGain.gain.value = 1 - this._config.mixRatio;

        // wet path: source → wetGain → worklet
        this._audioSource.connect(this._wetGain);
        this._wetGain.connect(this._noiseSuppressorNode);

        // dry path: source → dryGain
        this._audioSource.connect(this._dryGain);

        // merge → post chain → output gain → destination
        if (postChainHead) {
          this._noiseSuppressorNode.connect(postChainHead);
          this._dryGain.connect(postChainHead);
          this._connectPostChainTail(this._outputGainNode);
        } else {
          this._noiseSuppressorNode.connect(this._outputGainNode);
          this._dryGain.connect(this._outputGainNode);
        }

        console.log(`${LOG} 그래프: Source → [wet(${this._config.mixRatio})+dry(${1 - this._config.mixRatio})] → PostChain → Gain(${this._config.outputGain}) → Dest`);
      } else {
        // ── 100% RNNoise 모드 (단순 체인) ──
        this._audioSource.connect(this._noiseSuppressorNode);

        if (postChainHead) {
          this._noiseSuppressorNode.connect(postChainHead);
          this._connectPostChainTail(this._outputGainNode);
        } else {
          this._noiseSuppressorNode.connect(this._outputGainNode);
        }

        console.log(`${LOG} 그래프: Source → Worklet → PostChain → Gain(${this._config.outputGain}) → Dest`);
      }
    } else {
      // worklet 실패 → 바이패스 (후처리만 적용)
      console.warn(`${LOG} ⚠️ Worklet 없음 — 바이패스`);
      if (postChainHead) {
        this._audioSource.connect(postChainHead);
        this._connectPostChainTail(this._outputGainNode);
      } else {
        this._audioSource.connect(this._outputGainNode);
      }
    }

    this._outputGainNode.connect(this._audioDestination);
  }

  /**
   * 후처리 체인 (highpass → lowpass → compressor) 생성.
   * 활성화된 노드가 하나도 없으면 null 반환.
   */
  private _createPostProcessingChain(ctx: AudioContext): AudioNode | null {
    let head: AudioNode | null = null;

    // High-pass filter
    this._highPassNode = ctx.createBiquadFilter();
    this._highPassNode.type = 'highpass';
    if (this._config.highPassFilter) {
      this._highPassNode.frequency.value = this._config.highPassFrequency;
      this._highPassNode.Q.value = 0.707; // Butterworth
      console.log(`${LOG} HighPass: ${this._config.highPassFrequency}Hz`);
    } else {
      this._highPassNode.frequency.value = 0; // 전체 통과
    }
    head = this._highPassNode;

    // Low-pass filter
    this._lowPassNode = ctx.createBiquadFilter();
    this._lowPassNode.type = 'lowpass';
    if (this._config.lowPassFilter) {
      this._lowPassNode.frequency.value = this._config.lowPassFrequency;
      this._lowPassNode.Q.value = 0.707;
      console.log(`${LOG} LowPass: ${this._config.lowPassFrequency}Hz`);
    } else {
      this._lowPassNode.frequency.value = this._getLowPassBypassFrequency();
    }
    this._highPassNode.connect(this._lowPassNode);

    // Compressor
    this._compressorNode = ctx.createDynamicsCompressor();
    if (this._config.compressor) {
      this._compressorNode.threshold.value = this._config.compressorThreshold;
      this._compressorNode.knee.value = 12;
      this._compressorNode.ratio.value = 4;
      this._compressorNode.attack.value = 0.003;
      this._compressorNode.release.value = 0.25;
      console.log(`${LOG} Compressor: threshold=${this._config.compressorThreshold}dB`);
    } else {
      this._compressorNode.threshold.value = 0; // 압축 없음
    }
    this._lowPassNode.connect(this._compressorNode);

    return head;
  }

  /**
   * 후처리 체인의 마지막 노드를 target에 연결한다.
   */
  private _connectPostChainTail(target: AudioNode): void {
    // 체인 끝: compressor (항상 마지막)
    if (this._compressorNode) {
      this._compressorNode.connect(target);
    }
  }

  private async _initializeWorklet(): Promise<AudioWorkletNode | undefined> {
    if (!audioContext || audioContext.state === 'closed') {
      console.error(`${LOG} AudioContext 없음`);
      return undefined;
    }

    try {
      await audioContext.resume();
    } catch (e) {
      console.error(`${LOG} AudioContext resume 실패:`, e);
      return undefined;
    }

    const workletUrl = `${import.meta.env.BASE_URL}rnnoise/workletNode/NoiseSuppressorWorklet.js`;

    try {
      await audioContext.audioWorklet.addModule(workletUrl);
      console.log(`${LOG} ✅ Worklet 모듈 로드 성공`);
    } catch (e) {
      console.error(`${LOG} ❌ Worklet 모듈 로드 실패:`, e);
      return undefined;
    }

    try {
      const node = new AudioWorkletNode(audioContext, 'NoiseSuppressorWorklet', {
        processorOptions: {
          voiceGate: this._createVoiceGateConfig(),
        },
      });
      console.log(`${LOG} ✅ AudioWorkletNode 생성 성공`);
      return node;
    } catch (e) {
      console.error(`${LOG} ❌ AudioWorkletNode 생성 실패:`, e);
      return undefined;
    }
  }

  private _createVoiceGateConfig() {
    return {
      enabled: this._config.voiceGate,
      openThreshold: this._config.voiceGateOpenThreshold,
      closeThreshold: this._config.voiceGateCloseThreshold,
      minGain: this._config.voiceGateMinGain,
      hangoverMs: this._config.voiceGateHangoverMs,
      attackMs: this._config.voiceGateAttackMs,
      releaseMs: this._config.voiceGateReleaseMs,
    };
  }

  private _getLowPassBypassFrequency(): number {
    return Math.min(24000, (audioContext?.sampleRate ?? 48000) / 2);
  }
}
